// Typed wrappers for the configurable-limits surface.
//
// GET /v1/merchants/:id/payout-limits — viewer+ (any merchant member).
// PATCH /v1/merchants/:id/payout-limits/:provider — admin-only.
//
// Money handling (CLAUDE.md rule 6): every *_minor field is bigint INR
// paise end-to-end. The apiClient's json-bigint codec serialises bigint
// as a plain JSON integer on the wire; the BigInt() wrappers normalise
// the response from the json-bigint union type (number | bigint) to
// bigint at the deserialization boundary.
import { apiClient } from "./client";

/**
 * RawPayoutLimits — the JSON shape returned by /payout-limits.
 *
 * `source` flags which tier the values came from:
 *   "merchant" — per-merchant override row
 *   "platform" — platform default (merchant_id IS NULL)
 *   "compiled" — defensive fallback (no DB row matched)
 *
 * The panel uses this to render a "Using platform defaults" caption
 * vs a custom-override badge.
 */
interface RawPayoutLimits {
  id: number;
  provider: string;
  txn_min_minor: number | bigint;
  txn_max_minor: number | bigint;
  daily_client_minor: number | bigint;
  daily_merchant_minor: number | bigint;
  daily_account_minor: number | bigint;
  source: "merchant" | "platform" | "compiled";
}

/**
 * PayoutLimits — typed limits row exposed to UI consumers. All money
 * fields are bigint INR paise.
 */
export interface PayoutLimits {
  id: number;
  provider: string;
  txn_min_minor: bigint;
  txn_max_minor: bigint;
  daily_client_minor: bigint;
  daily_merchant_minor: bigint;
  daily_account_minor: bigint;
  source: "merchant" | "platform" | "compiled";
}

function normalise(row: RawPayoutLimits): PayoutLimits {
  return {
    ...row,
    txn_min_minor: BigInt(row.txn_min_minor),
    txn_max_minor: BigInt(row.txn_max_minor),
    daily_client_minor: BigInt(row.daily_client_minor),
    daily_merchant_minor: BigInt(row.daily_merchant_minor),
    daily_account_minor: BigInt(row.daily_account_minor),
  };
}

/**
 * listPayoutLimits — GET /v1/merchants/:merchantId/payout-limits.
 *
 * Returns one row per known provider; the backend resolves
 * per-merchant > platform default > compiled fallback per row so the
 * panel always has a stable set even for merchants without any
 * overrides yet.
 */
export async function listPayoutLimits(merchantId: number): Promise<PayoutLimits[]> {
  const resp = await apiClient.get<{ limits: RawPayoutLimits[] }>(
    `/v1/merchants/${merchantId}/payout-limits`,
  );
  return resp.data.limits.map(normalise);
}

/**
 * UpdatePayoutLimitsPayload — PATCH body. Every field is optional —
 * sending {"txn_max_minor": 5000000n} only raises the ceiling, the
 * other four columns stay put.
 *
 * All amounts are bigint INR paise; the apiClient transforms via
 * json-bigint so they hit the wire as plain JSON integers.
 */
export interface UpdatePayoutLimitsPayload {
  txn_min_minor?: bigint;
  txn_max_minor?: bigint;
  daily_client_minor?: bigint;
  daily_merchant_minor?: bigint;
  daily_account_minor?: bigint;
}

/**
 * updatePayoutLimits — PATCH /v1/merchants/:merchantId/payout-limits/:provider.
 *
 * Admin-only. Upserts the per-merchant row: creates it on first call
 * (seeded from the platform default for unspecified fields), updates
 * only the supplied fields thereafter. Each call writes one audit row.
 *
 * Backend invariants enforced:
 *   - txn_min ≤ txn_max (422 with code=txn_band_inverted otherwise)
 *   - every positive bound > 0 (txn_min may be 0 to allow "no floor")
 */
export async function updatePayoutLimits(
  merchantId: number,
  provider: string,
  payload: UpdatePayoutLimitsPayload,
): Promise<PayoutLimits> {
  const resp = await apiClient.patch<RawPayoutLimits>(
    `/v1/merchants/${merchantId}/payout-limits/${provider}`,
    payload,
  );
  return normalise(resp.data);
}

/**
 * EffectivePayoutLimits — aggregated limits across all the merchant's
 * enabled providers (PDF round 3). Surfaced in the Upload Payouts modal
 * so operators see the bounds their batch will be validated against.
 *
 * Aggregation (server-side):
 *   txn_min        — MAX of enabled providers' mins (most restrictive)
 *   txn_max        — MIN of enabled providers' maxes (most restrictive)
 *   daily_client   — SUM
 *   daily_merchant — SUM
 *   daily_account  — SUM, ADMIN ONLY (undefined for merchant callers)
 */
export interface EffectivePayoutLimits {
  txn_min_minor: bigint;
  txn_max_minor: bigint;
  daily_client_minor: bigint;
  daily_merchant_minor: bigint;
  /** Only present for admin callers. */
  daily_account_minor?: bigint;
  /** Sorted list of provider keys that contributed to the aggregate. */
  providers: string[];
}

/**
 * getEffectivePayoutLimits — GET /v1/merchants/:merchantId/payout-limits/effective
 */
export async function getEffectivePayoutLimits(
  merchantId: number,
): Promise<EffectivePayoutLimits> {
  const resp = await apiClient.get<{
    txn_min_minor: number | bigint;
    txn_max_minor: number | bigint;
    daily_client_minor: number | bigint;
    daily_merchant_minor: number | bigint;
    daily_account_minor?: number | bigint;
    providers: string[];
  }>(`/v1/merchants/${merchantId}/payout-limits/effective`);
  return {
    txn_min_minor: BigInt(resp.data.txn_min_minor),
    txn_max_minor: BigInt(resp.data.txn_max_minor),
    daily_client_minor: BigInt(resp.data.daily_client_minor),
    daily_merchant_minor: BigInt(resp.data.daily_merchant_minor),
    daily_account_minor:
      resp.data.daily_account_minor != null
        ? BigInt(resp.data.daily_account_minor)
        : undefined,
    providers: resp.data.providers ?? [],
  };
}
